import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment/moment';
import { enumValue, formatDate } from '~/util';
import { ReminderModal, ReminderStatus, ReminderWarningIcon } from '~/components/Reminders';
import { PopoverContent } from '~/components/SearchResults';
import { BSOverlay } from '~/components/Bootstrap';
import { OverlayInjectedProps, OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger';
import { Popover } from 'react-bootstrap';

interface ReminderRowProps {
    reminder: ReminderType;
}

export function ReminderRow({ reminder }: ReminderRowProps) {
    const isReminderOverdue = React.useMemo(() => {
        if (reminder.status !== enumValue(['task', 'status', 'Not Started'])) return false;
        const today = moment().utc(true).hour(0).minute(0).second(0);
        return moment(reminder.due_at).isBefore(today, 'day');
    }, [reminder.due_at, reminder.status]);

    const renderTooltip = (props: OverlayInjectedProps) => {
        return (
            <Popover {...props}>
                <Popover.Body>
                    <PopoverContent patient={reminder.patient} />
                </Popover.Body>
            </Popover>
        );
    };
    const overlayProps = {
        placement: 'top-start',
        delay: { show: 1500, hide: 0 },
        overlay: renderTooltip,
    } as OverlayTriggerProps;

    return (
        <tr className="align-middle">
            <BSOverlay {...overlayProps}>
                <td data-name="fname">
                    <Link to={`/patients/${reminder.patient.id}`}>
                        {reminder.patient.first_name}
                    </Link>
                </td>
            </BSOverlay>
            <BSOverlay {...overlayProps}>
                <td data-name="lname">
                    <Link to={`/patients/${reminder.patient.id}`}>
                        {reminder.patient.last_name}
                    </Link>
                </td>
            </BSOverlay>
            <BSOverlay {...overlayProps}>
                <td data-name="dob">
                    <Link to={`/patients/${reminder.patient.id}`}>
                        {formatDate(reminder.patient.dob)}
                    </Link>
                </td>
            </BSOverlay>
            <td className={isReminderOverdue ? 'text-danger' : ''}>
                {formatDate(reminder.due_at)}
                <ReminderWarningIcon reminder={reminder} />
            </td>
            <td className="text-truncate" title={reminder.description}>
                {reminder.description}
            </td>
            <td>
                <div className="d-flex align-items-center justify-content-between">
                    <ReminderStatus reminder={reminder} />
                    {reminder.timely_action ? (
                        <i className="fa-regular fa-clock" title="Timely Action">
                            <span className="sr-only">Timely Action</span>
                        </i>
                    ) : (
                        <i className="fa-regular fa-bell" title="User Created Reminder">
                            <span className="sr-only">User Created Reminder</span>
                        </i>
                    )}
                </div>
            </td>
            <td className="text-center">
                <ReminderModal reminder={reminder} patient={reminder.patient} />
                <span className="sr-only">Action</span>
            </td>
        </tr>
    );
}
